import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Avatar,
  AvatarFallback,
} from "src/components/ui/avatar";
import { Input } from "src/components/ui/input";
import { Button } from "src/components/ui/button";
import { Badge } from "src/components/ui/badge";
import { ScrollArea } from "src/components/ui/scroll-area";
import { Skeleton } from "src/components/ui/skeleton";
import { Search, User, Phone } from "lucide-react";
import { cn } from "src/shared/lib/utils/utils";
import { apiRequests } from 'src/shared/api/api';
import { toast } from "sonner";

const ContragentChainModal = ({ 
  visible, 
  onCancel, 
  onSuccess,
  chatId,
  token,
  currentContragentId 
}) => {
  const [searchText, setSearchText] = useState('');
  const [contragents, setContragents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContragent, setSelectedContragent] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const loadContragents = useCallback(async (search = '', pageNum = 1) => {
    try {
      setLoading(true);
      const params = {
        token,
        limit: pageSize,
        offset: (pageNum - 1) * pageSize,
        ...(search && { search: search })
      };

      const response = await apiRequests.contragents.getItems(params);
      
      let filteredContragents = response.data?.result || [];
      const totalCount = response.data?.count || 0;

      if (currentContragentId) {
        filteredContragents = filteredContragents.filter(
          c => c.id !== currentContragentId
        );
      }
      
      setContragents(filteredContragents);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (error) {
      console.error('Ошибка загрузки контрагентов:', error);
      toast.error('Не удалось загрузить контрагентов');
      setContragents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, currentContragentId]);

  useEffect(() => {
    if (visible && token) {
      loadContragents();
    } else {
      setSearchText('');
      setSelectedContragent(null);
      setPage(1);
    }
  }, [visible, token, loadContragents]);

  const handleSearch = async () => {
    setPage(1);
    setSearchLoading(true);
    await loadContragents(searchText, 1);
    setSearchLoading(false);
  };

  const handleChainContragent = async () => {
    if (!selectedContragent || !chatId) return;

    try {
      const payload = {
        phone: selectedContragent.phone || '',
        name: selectedContragent.name
      };

      const response = await apiRequests.chats.chainClient(chatId, payload);
      
      if (response.status === 200) {
        toast.success('Контрагент успешно привязан к чату');
        onSuccess(selectedContragent);
        handleCancel();
      }
    } catch (error) {
      console.error('Ошибка привязки контрагента:', error);
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.message || 
                      'Ошибка привязки контрагента';
      toast.error(errorMsg);
    }
  };

  const handleCancel = () => {
    setSearchText('');
    setSelectedContragent(null);
    setPage(1);
    onCancel();
  };

  const handlePageChange = (newPage) => {
    loadContragents(searchText, newPage);
  };

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            Привязать контрагента к чату
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-6 space-y-4">
            {/* Поиск и фильтры */}
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Поиск контрагента по имени, телефону или email"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    disabled={loading}
                    className="pl-10"
                  />
                </div>
                <Button 
                  onClick={handleSearch}
                  loading={searchLoading}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Поиск
                </Button>
              </div>
              
              {currentContragentId && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    У чата уже есть привязанный контрагент
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Можно привязать другого контрагента
                  </span>
                </div>
              )}
            </div>

            {/* Таблица контрагентов */}
            <div className="border rounded-lg overflow-hidden">
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead className="w-[40%]">Контрагент</TableHead>
                      <TableHead className="w-[30%]">Телефон</TableHead>
                      <TableHead className="w-[20%]">Email</TableHead>
                      <TableHead className="w-[10%] text-right">Действие</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-8 w-8 rounded-full" />
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-24" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                          <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                        </TableRow>
                      ))
                    ) : contragents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="h-32 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <User className="h-8 w-8 text-muted-foreground mb-2" />
                            <p className="text-muted-foreground">Контрагенты не найдены</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      contragents.map((contragent) => (
                        <TableRow 
                          key={contragent.id}
                          className={cn(
                            "cursor-pointer hover:bg-accent/50",
                            selectedContragent?.id === contragent.id && "bg-accent"
                          )}
                          onClick={() => setSelectedContragent(contragent)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {contragent.name?.[0]?.toUpperCase() || 'C'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{contragent.name}</div>
                                {contragent.description && (
                                  <div className="text-sm text-muted-foreground">
                                    {contragent.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {contragent.phone ? (
                              <div className="flex items-center gap-2">
                                <Phone className="h-3 w-3" />
                                <span>{contragent.phone}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Не указан</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {contragent.email || <span className="text-muted-foreground">Не указан</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant={selectedContragent?.id === contragent.id ? "default" : "outline"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContragent(contragent);
                              }}
                            >
                              {selectedContragent?.id === contragent.id ? 'Выбран' : 'Выбрать'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>

            {/* Пагинация */}
            {total > 0 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Показано {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} из {total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1 || loading}
                  >
                    Назад
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === page || p === page - 1 || p === page + 1 || p === Math.ceil(total / pageSize))
                      .map((p, i, arr) => (
                        <React.Fragment key={p}>
                          {i > 0 && arr[i - 1] !== p - 1 && <span className="px-2">...</span>}
                          <Button
                            variant={page === p ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(p)}
                            disabled={loading}
                          >
                            {p}
                          </Button>
                        </React.Fragment>
                      ))
                    }
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= Math.ceil(total / pageSize) || loading}
                  >
                    Вперед
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Отмена
          </Button>
          <Button
            onClick={handleChainContragent}
            disabled={!selectedContragent}
          >
            Привязать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContragentChainModal;